import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../ui/Button";
import { PasswordField } from "../../ui/PasswordField";
import { ImageUpload } from "../../ui/ImageUpload";
import { useToast } from "../../context/ToastContext";
import {
  getCurrentUser,
  updateCurrentUser,
  changePassword,
} from "../../Utils/authService";
import * as S from "./AccountSettings.styled";

import SocialAuthButtons from "../Login/SocialAuth";

const EMPTY_PROFILE = {
  firstName: "",
  lastName: "",
  phoneNumber: "",
  profilePictureUrl: "",
};

const EMPTY_PASSWORD = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

/**
 * Lets a signed-in user edit their own details and change their password. The backend has
 * supported both since `PUT /api/Users/me` and `POST /api/Users/me/change-password` existed;
 * there was simply no screen that called them.
 */
const AccountSettings = () => {
  const { t } = useTranslation();
  const { success: notifySuccess, error: notifyError } = useToast();

  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);

  const [passwords, setPasswords] = useState(EMPTY_PASSWORD);
  const [passwordErrors, setPasswordErrors] = useState({});
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    let active = true;
    getCurrentUser()
      .then((user) => {
        if (!active || !user) return;
        setEmail(user.email || "");
        setProfile({
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          phoneNumber: user.phoneNumber || "",
          profilePictureUrl: user.profilePictureUrl || "",
        });
      })
      .catch(() => {
        if (active) {
          notifyError(
            t("account.load_failed", { defaultValue: "Could not load your account." })
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // Runs once: re-fetching on every toast identity change would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (key, value) => setProfile((prev) => ({ ...prev, [key]: value }));

  const handleSaveProfile = async (event) => {
    event.preventDefault();
    if (!profile.firstName.trim()) {
      notifyError(
        t("account.first_name_required", { defaultValue: "First name is required." })
      );
      return;
    }

    setSavingProfile(true);
    try {
      await updateCurrentUser({
        firstName: profile.firstName.trim(),
        lastName: profile.lastName.trim(),
        phoneNumber: profile.phoneNumber.trim(),
        profilePictureUrl: profile.profilePictureUrl || "",
      });
      notifySuccess(t("account.saved", { defaultValue: "Your details were updated." }));
    } catch (error) {
      notifyError(
        error?.response?.data?.[0] ||
          error?.response?.data?.message ||
          t("account.save_failed", { defaultValue: "Could not save your details." })
      );
    } finally {
      setSavingProfile(false);
    }
  };

  const validatePasswords = useCallback(
    (values) => {
      const next = {};
      const required = t("login.field_required", { defaultValue: "This field is required" });

      if (!values.currentPassword) next.currentPassword = required;

      if (!values.newPassword) next.newPassword = required;
      else if (values.newPassword.length < 10) {
        next.newPassword = t("login.password_too_short", {
          defaultValue: "Password must be at least 10 characters.",
        });
      } else if (
        !/[A-Z]/.test(values.newPassword) ||
        !/[a-z]/.test(values.newPassword) ||
        !/[0-9]/.test(values.newPassword) ||
        !/[^a-zA-Z0-9]/.test(values.newPassword)
      ) {
        next.newPassword = t("login.password_weak", {
          defaultValue: "Add an uppercase, lowercase, number, and special character.",
        });
      }

      if (!values.confirmPassword) next.confirmPassword = required;
      else if (values.confirmPassword !== values.newPassword) {
        next.confirmPassword = t("login.passwords_mismatch", {
          defaultValue: "Passwords do not match.",
        });
      }

      return next;
    },
    [t]
  );

  const handleChangePassword = async (event) => {
    event.preventDefault();
    const validation = validatePasswords(passwords);
    setPasswordErrors(validation);
    if (Object.keys(validation).length > 0) return;

    setSavingPassword(true);
    try {
      await changePassword({
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });
      setPasswords(EMPTY_PASSWORD);
      notifySuccess(
        t("account.password_changed", { defaultValue: "Your password was changed." })
      );
    } catch (error) {
      notifyError(
        error?.response?.data?.[0] ||
          t("account.password_failed", {
            defaultValue: "Could not change your password. Check your current password.",
          })
      );
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <S.Page>
        <S.Inner aria-busy="true">
          <S.Skeleton aria-hidden="true" />
          <S.Skeleton aria-hidden="true" />
        </S.Inner>
      </S.Page>
    );
  }

  return (
    <S.Page>
      <S.Inner>
        <S.Title>{t("account.title", { defaultValue: "Account settings" })}</S.Title>

        <SocialAuthButtons linkAccount />
        <S.Card as="form" onSubmit={handleSaveProfile}>
          <S.CardTitle>{t("account.details", { defaultValue: "Your details" })}</S.CardTitle>

          <S.Row>
            <S.Field>
              {t("account.first_name", { defaultValue: "First name" })}
              <S.TextInput
                value={profile.firstName}
                maxLength={100}
                autoComplete="given-name"
                onChange={(e) => setField("firstName", e.target.value)}
              />
            </S.Field>
            <S.Field>
              {t("account.last_name", { defaultValue: "Last name" })}
              <S.TextInput
                value={profile.lastName}
                maxLength={100}
                autoComplete="family-name"
                onChange={(e) => setField("lastName", e.target.value)}
              />
            </S.Field>
          </S.Row>

          <S.Row>
            <S.Field>
              {t("account.phone", { defaultValue: "Phone number" })}
              <S.TextInput
                type="tel"
                value={profile.phoneNumber}
                maxLength={30}
                autoComplete="tel"
                onChange={(e) => setField("phoneNumber", e.target.value)}
              />
            </S.Field>
            <S.Field as="div">
              {t("account.email", { defaultValue: "Email" })}
              <S.ReadOnlyValue>{email}</S.ReadOnlyValue>
              <S.CardHint>
                {t("account.email_locked", {
                  defaultValue:
                    "Your email is your sign-in identity and cannot be changed here.",
                })}
              </S.CardHint>
            </S.Field>
          </S.Row>

          <S.Field as="div">
            {t("account.photo", { defaultValue: "Profile photo" })}
            {/* A profile photo is a file the person uploads. A pasted link would
                hotlink somebody else's server and break when that server does. */}
            <ImageUpload
              label=""
              currentUrl={profile.profilePictureUrl}
              folder="profiles"
              allowUrl={false}
              onUpload={(url) => setField("profilePictureUrl", url)}
            />
            {profile.profilePictureUrl ? (
              <S.Actions>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setField("profilePictureUrl", "")}
                >
                  {t("account.remove_photo", { defaultValue: "Remove photo" })}
                </Button>
              </S.Actions>
            ) : null}
          </S.Field>

          <S.Actions>
            <Button type="submit" variant="primary" disabled={savingProfile}>
              {savingProfile
                ? t("account.saving", { defaultValue: "Saving…" })
                : t("account.save", { defaultValue: "Save changes" })}
            </Button>
          </S.Actions>
        </S.Card>

        <S.Card as="form" onSubmit={handleChangePassword}>
          <S.CardTitle>
            {t("account.password_title", { defaultValue: "Change password" })}
          </S.CardTitle>
          <S.CardHint>
            {t("account.password_hint", {
              defaultValue:
                "Forgot it instead? Sign out and use the reset link on the login page.",
            })}
          </S.CardHint>

          <PasswordField
            id="currentPassword"
            name="currentPassword"
            label={t("account.current_password", { defaultValue: "Current password" })}
            autoComplete="current-password"
            value={passwords.currentPassword}
            onChange={(e) =>
              setPasswords((prev) => ({ ...prev, currentPassword: e.target.value }))
            }
            errorText={passwordErrors.currentPassword}
          />
          <PasswordField
            id="newPassword"
            name="newPassword"
            label={t("account.new_password", { defaultValue: "New password" })}
            autoComplete="new-password"
            value={passwords.newPassword}
            onChange={(e) =>
              setPasswords((prev) => ({ ...prev, newPassword: e.target.value }))
            }
            errorText={passwordErrors.newPassword}
          />
          <PasswordField
            id="confirmPassword"
            name="confirmPassword"
            label={t("account.confirm_password", { defaultValue: "Confirm new password" })}
            autoComplete="new-password"
            value={passwords.confirmPassword}
            onChange={(e) =>
              setPasswords((prev) => ({ ...prev, confirmPassword: e.target.value }))
            }
            errorText={passwordErrors.confirmPassword}
          />

          <S.Actions>
            <Button type="submit" variant="primary" disabled={savingPassword}>
              {savingPassword
                ? t("account.saving", { defaultValue: "Saving…" })
                : t("account.change_password", { defaultValue: "Change password" })}
            </Button>
          </S.Actions>
        </S.Card>
      </S.Inner>
    </S.Page>
  );
};

export default AccountSettings;
